import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import emailjs from 'emailjs-com';
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCheck,
  FaPaperPlane,
  FaExclamationCircle,
  FaSpinner,
  FaLinkedin,
  FaGithub,
  FaTwitter,
  FaWhatsapp,
} from 'react-icons/fa';
import { toast, Toaster } from 'react-hot-toast';

function Icon(component: any, className?: string) {
  return React.createElement(component as React.ComponentType<{ className?: string }>, { className });
}

const contactInfo = [
  {
    icon: FaEnvelope,
    label: 'Email',
    value: 'evodemuyisingize@gmail.com',
    href: 'mailto:evodemuyisingize@gmail.com',
  },
  {
    icon: FaPhone,
    label: 'Phone',
    value: '+250 791 783 308',
    href: 'tel:+250791783308',
  },
  {
    icon: FaMapMarkerAlt,
    label: 'Location',
    value: 'Kigali, Rwanda',
    href: '',
  },
];

const socials = [
  { icon: FaLinkedin, href: 'https://www.linkedin.com/in/evode-muyisingizemwese', label: 'LinkedIn', color: 'hover:text-[#0A66C2]' },
  { icon: FaGithub, href: 'https://github.com/evodenuby', label: 'GitHub', color: 'hover:text-textPrimary' },
  { icon: FaTwitter, href: 'https://twitter.com/evodeSTACK', label: 'Twitter', color: 'hover:text-[#1DA1F2]' },
  { icon: FaWhatsapp, href: 'https://wa.me/250791783308', label: 'WhatsApp', color: 'hover:text-[#25D366]' },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const Contact: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState({ submitting: false, submitted: false, error: false });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.name.trim() || formData.name.length < 2) e.name = 'Name is required (min 2 chars)';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Valid email is required';
    if (!formData.subject.trim() || formData.subject.length < 3) e.subject = 'Subject is required (min 3 chars)';
    if (formData.message.length < 20) e.message = 'Message must be at least 20 characters';
    if (formData.message.length > 500) e.message = 'Message cannot exceed 500 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => { const n = { ...p }; delete n[name]; return n; });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) { toast.error('Please fix the errors in the form'); return; }
    setStatus({ submitting: true, submitted: false, error: false });
    try {
      await emailjs.send(
        'service_oltlyo4',
        'template_ent95ay',
        { name: formData.name, email: formData.email, title: formData.subject, message: formData.message, to_email: 'evodemuyisingize@gmail.com', date: new Date().toLocaleDateString(), time: new Date().toLocaleTimeString() },
        '0RySkcUL6J_eYZWFB'
      );
      setStatus({ submitting: false, submitted: true, error: false });
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus((p) => ({ ...p, submitted: false })), 6000);
    } catch {
      setStatus({ submitting: false, submitted: false, error: true });
      toast.error('Failed to send message. Please try again.');
    }
  };

  const inputClass = (field: string) =>
    `w-full bg-white dark:bg-[#161b22] border ${errors[field] ? 'border-red-500' : 'border-border/50'} text-textPrimary text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-secondary/60 focus:ring-1 focus:ring-secondary/20 placeholder:text-textSecondary/40 transition-all duration-300`;

  return (
    <section id="contact" className="py-14 md:py-20 bg-gradient-to-b from-primary to-tertiary/30 relative overflow-hidden">
      <Toaster
        position="top-right"
        toastOptions={{ duration: 4000, style: { background: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' } }}
      />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(var(--color-accent) 1px, transparent 1px), linear-gradient(90deg, var(--color-accent) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div ref={ref} variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} className="max-w-5xl mx-auto">

          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-border/50 mb-5">
              {Icon(FaEnvelope, 'text-xs text-secondary')}
              <span className="font-mono text-[11px] text-textSecondary/60 tracking-wider uppercase">Contact</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-textPrimary">
              Get In{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Touch</span>
            </h2>
            <p className="text-textSecondary text-sm mt-3 max-w-md mx-auto">
              Have a project in mind or want to collaborate? I'd love to hear from you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">

            {/* ── Left: Info ── */}
            <motion.div variants={itemVariants} className="lg:col-span-2 space-y-6">
              {/* Contact details */}
              <div className="p-6 rounded-2xl bg-white dark:bg-[#161b22] border border-border/40 backdrop-blur-xl space-y-5">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-center gap-4 group">
                    <div className="w-10 h-10 rounded-lg bg-[#f0f7ff] dark:bg-[#1c2333] border border-border/30 flex items-center justify-center group-hover:border-secondary/40 transition-colors duration-300">
                      {Icon(item.icon, 'text-sm text-secondary')}
                    </div>
                    <div>
                      <div className="text-[11px] font-mono text-textSecondary/50 uppercase tracking-wider">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-sm font-medium text-textPrimary hover:text-secondary transition-colors duration-300">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-sm font-medium text-textPrimary">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social links */}
              <div className="p-6 rounded-2xl bg-white dark:bg-[#161b22] border border-border/40 backdrop-blur-xl">
                <div className="text-[11px] font-mono text-textSecondary/50 uppercase tracking-wider mb-4">Follow Me</div>
                <div className="flex gap-3">
                  {socials.map((s) => (
                    <motion.a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ y: -2, scale: 1.1 }}
                      className={`w-10 h-10 rounded-lg bg-[#f6f8fa] dark:bg-[#21262d] border border-border/30 flex items-center justify-center text-textSecondary/60 transition-all duration-300 ${s.color}`}
                      aria-label={s.label}
                    >
                      {Icon(s.icon, 'text-base')}
                    </motion.a>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* ── Right: Form ── */}
            <motion.div variants={itemVariants} className="lg:col-span-3">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="relative p-6 md:p-8 rounded-2xl bg-white dark:bg-[#161b22] border border-border/40 backdrop-blur-xl">
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-xs font-medium text-textSecondary mb-1.5 block">Name *</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your name" className={inputClass('name')} />
                        {errors.name && <p className="mt-1 text-xs text-red-500 flex items-center gap-1">{Icon(FaExclamationCircle, 'text-[10px]')}{errors.name}</p>}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-textSecondary mb-1.5 block">Email *</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className={inputClass('email')} />
                        {errors.email && <p className="mt-1 text-xs text-red-500 flex items-center gap-1">{Icon(FaExclamationCircle, 'text-[10px]')}{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-textSecondary mb-1.5 block">Subject *</label>
                      <input type="text" name="subject" value={formData.subject} onChange={handleChange} placeholder="Project Inquiry" className={inputClass('subject')} />
                      {errors.subject && <p className="mt-1 text-xs text-red-500 flex items-center gap-1">{Icon(FaExclamationCircle, 'text-[10px]')}{errors.subject}</p>}
                    </div>

                    <div>
                      <label className="text-xs font-medium text-textSecondary mb-1.5 block">
                        Message * <span className="text-textSecondary/40 ml-1">({formData.message.length}/500)</span>
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell me about your project..."
                        rows={5}
                        maxLength={500}
                        className={`${inputClass('message')} resize-none`}
                      />
                      {errors.message && <p className="mt-1 text-xs text-red-500 flex items-center gap-1">{Icon(FaExclamationCircle, 'text-[10px]')}{errors.message}</p>}
                    </div>

                    <motion.button
                      type="submit"
                      disabled={status.submitting || formData.message.length < 20}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-secondary/25 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="flex items-center justify-center gap-2">
                        {status.submitting ? (
                          <>{Icon(FaSpinner, 'animate-spin')} Sending...</>
                        ) : (
                          <>{Icon(FaPaperPlane)} Send Message</>
                        )}
                      </span>
                    </motion.button>

                    {status.submitted && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-3 rounded-lg bg-success/10 border border-success/20">
                        <p className="text-success text-sm text-center flex items-center justify-center gap-2">
                          {Icon(FaCheck)} Message sent! I'll get back to you soon.
                        </p>
                      </motion.div>
                    )}

                    {status.error && (
                      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="p-3 rounded-lg bg-danger/10 border border-danger/20">
                        <p className="text-danger text-sm text-center flex items-center justify-center gap-2">
                          {Icon(FaExclamationCircle)} Failed to send. Please try again or email me directly.
                        </p>
                      </motion.div>
                    )}
                  </form>
                </div>
              </div>
            </motion.div>

          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
